import React, { useMemo, useState, useEffect } from 'react';
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Polygon,
  Popup,
  LayersControl,
  LayerGroup,
  useMap,
  ZoomControl,
  AttributionControl,
} from 'react-leaflet';
import { api } from '../lib/api';

const { BaseLayer, Overlay } = LayersControl;

const STATE_COLORS = { calm: '#7ecfc0', watch: '#e8c878', alert: '#e8a598', severe: '#e87878' };

function FitBounds({ regions }) {
  const map = useMap();
  React.useEffect(() => {
    if (regions?.length > 0) {
      const bounds = regions.map(r => [r.lat, r.lon]);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 4 });
    }
  }, [map, regions]);
  return null;
}

function StateLegend() {
  return (
    <div className="map-legend" data-testid="map-legend">
      <div className="map-legend-title">STATE</div>
      <div className="map-legend-items">
        {['calm', 'watch', 'alert', 'severe'].map((s) => (
          <div key={s} className="map-legend-item">
            <span className="map-legend-dot" style={{ background: STATE_COLORS[s], boxShadow: `0 0 6px ${STATE_COLORS[s]}` }} />
            <span>{s.toUpperCase()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatTs(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
  } catch (e) {
    return iso;
  }
}

export default function MapPanel({ regions, events, selectedEventId, selectedRegionId, onSelectEvent, onSelectRegion }) {
  const [showSubregions, setShowSubregions] = useState(true);
  const [mangroveAois, setMangroveAois] = useState([]);

  // Environmental overlay placeholders — activated when OWM key present.
  const owmKey = process.env.REACT_APP_OWM_KEY || '';

  useEffect(() => {
    api.get('/api/karbon/aois')
      .then(r => setMangroveAois(r.data.aois || []))
      .catch(() => setMangroveAois([]));
  }, []);

  const topEvents = useMemo(() => {
    return (events || []).filter(e => {
      const loc = e.location;
      return loc && (loc.lat !== 0 || loc.lon !== 0) && !e.id?.startsWith('om-');
    }).slice(0, 40);
  }, [events]);

  const subregions = useMemo(() => {
    return (regions || []).filter(r => r.has_subregions).flatMap(r => r.subregions || []);
  }, [regions]);

  return (
    <div className="map-panel" data-testid="map-panel">
      <div className="map-toolbar">
        <span className="map-title" data-testid="map-title">VIRTUAL SKIN · GEOSPATIAL OPERATIONS</span>
        <div className="map-toggles">
          <button
            className={`map-toggle-btn${showSubregions ? ' active' : ''}`}
            onClick={() => setShowSubregions(!showSubregions)}
            data-testid="toggle-subregions"
          >
            {showSubregions ? 'HIDE' : 'SHOW'} ZONES
          </button>
        </div>
      </div>
      <div className="map-container" data-testid="map-container">
        <MapContainer
          center={[20, -40]}
          zoom={2}
          minZoom={2}
          maxZoom={12}
          style={{ width: '100%', height: '100%', background: '#03080a' }}
          zoomControl={false}
          attributionControl={false}
          scrollWheelZoom={true}
          touchZoom={true}
          tap={true}
          worldCopyJump={true}
        >
          <ZoomControl position="bottomright" />
          <AttributionControl position="bottomleft" prefix={false} />

          <LayersControl position="topright">
            {/* ── BASE LAYERS ───────────────────────────────── */}
            <BaseLayer checked name="Dark · Carto">
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
                attribution='&copy; CARTO'
              />
            </BaseLayer>
            <BaseLayer name="Satellite · Esri">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution='Esri, Maxar, Earthstar Geographics'
              />
            </BaseLayer>
            <BaseLayer name="Terrain · OpenTopo">
              <TileLayer
                url="https://tile.opentopomap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenTopoMap'
                maxZoom={17}
              />
            </BaseLayer>
            <BaseLayer name="Ocean · Esri">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}"
                attribution='Esri Ocean'
              />
            </BaseLayer>

            {/* ── ENVIRONMENTAL OVERLAYS ────────────────────── */}
            {owmKey && (
              <Overlay name="Clouds">
                <TileLayer
                  url={`https://tile.openweathermap.org/map/clouds_new/{z}/{x}/{y}.png?appid=${owmKey}`}
                  opacity={0.6}
                />
              </Overlay>
            )}
            {owmKey && (
              <Overlay name="Precipitation">
                <TileLayer
                  url={`https://tile.openweathermap.org/map/precipitation_new/{z}/{x}/{y}.png?appid=${owmKey}`}
                  opacity={0.6}
                />
              </Overlay>
            )}
            {owmKey && (
              <Overlay name="Wind">
                <TileLayer
                  url={`https://tile.openweathermap.org/map/wind_new/{z}/{x}/{y}.png?appid=${owmKey}`}
                  opacity={0.55}
                />
              </Overlay>
            )}
            {owmKey && (
              <Overlay name="Temperature">
                <TileLayer
                  url={`https://tile.openweathermap.org/map/temp_new/{z}/{x}/{y}.png?appid=${owmKey}`}
                  opacity={0.5}
                />
              </Overlay>
            )}

            {/* Rainviewer radar (free, no key required) */}
            <Overlay name="Rain Radar (live)">
              <TileLayer
                url="https://tilecache.rainviewer.com/v2/radar/nowcast_0/512/{z}/{x}/{y}/2/1_1.png"
                opacity={0.6}
              />
            </Overlay>

            {/* ── SIGNAL LAYERS ─────────────────────────────── */}
            <Overlay checked name="Watch Regions">
              <LayerGroup>
                {(regions || []).map((r) => {
                  const col = STATE_COLORS[r.state] || '#7ecfc0';
                  const isPrimary = r.type === 'primary';
                  const isSelected = r.id === selectedRegionId;
                  const lastUpdate = r.last_update || r.updated_at || r.timestamp;
                  return (
                    <CircleMarker
                      key={r.id}
                      center={[r.lat, r.lon]}
                      radius={isSelected ? 16 : isPrimary ? 11 : 8}
                      pathOptions={{
                        color: col,
                        fillColor: col,
                        fillOpacity: isSelected ? 0.75 : 0.5,
                        weight: isSelected ? 3 : isPrimary ? 2 : 1.5,
                      }}
                      eventHandlers={{ click: () => onSelectRegion?.(r.id) }}
                    >
                      <Popup maxWidth={320} keepInView>
                        <div className="signal-popup" data-testid={`popup-region-${r.id}`}>
                          <div className="signal-popup-head">
                            <span className="signal-popup-dot" style={{ background: col, boxShadow: `0 0 8px ${col}` }} />
                            <span className="signal-popup-name">{r.name}</span>
                          </div>
                          <div className="signal-popup-state" style={{ color: col }}>
                            {r.state?.toUpperCase()}
                          </div>
                          <div className="signal-popup-grid">
                            <div>Signals</div><div>{r.signal_count ?? '—'}</div>
                            {r.weather?.temperature_c != null && (<>
                              <div>Temp</div><div>{r.weather.temperature_c}°C</div>
                            </>)}
                            {r.weather?.weather_desc && (<>
                              <div>Cond.</div><div>{r.weather.weather_desc}</div>
                            </>)}
                            {r.weather?.wind_kph != null && (<>
                              <div>Wind</div><div>{r.weather.wind_kph} kph</div>
                            </>)}
                            {r.confidence != null && (<>
                              <div>Conf.</div><div>{(r.confidence * 100).toFixed(0)}%</div>
                            </>)}
                          </div>
                          {r.reason && <div className="signal-popup-reason">{r.reason}</div>}
                          {lastUpdate && <div className="signal-popup-ts">Updated · {formatTs(lastUpdate)}</div>}
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </LayerGroup>
            </Overlay>

            {/* Subregions / Analysis Zones */}
            <Overlay checked={showSubregions} name="Analysis Zones">
              <LayerGroup>
                {subregions.map((sr) => (
                  <CircleMarker
                    key={sr.id}
                    center={[sr.lat, sr.lon]}
                    radius={5}
                    pathOptions={{
                      color: STATE_COLORS[sr.state] || '#00e5b0',
                      fillColor: STATE_COLORS[sr.state] || '#00e5b0',
                      fillOpacity: 0.4,
                      weight: 1.5,
                    }}
                  >
                    <Popup maxWidth={280}>
                      <div className="signal-popup">
                        <div className="signal-popup-head">
                          <span className="signal-popup-dot" style={{ background: STATE_COLORS[sr.state] || '#00e5b0' }} />
                          <span className="signal-popup-name">{sr.name}</span>
                        </div>
                        <div className="signal-popup-state" style={{ color: STATE_COLORS[sr.state] || '#00e5b0' }}>
                          {sr.state?.toUpperCase()} · ANALYSIS ZONE
                        </div>
                        {sr.weather?.temperature_c != null && (
                          <div className="signal-popup-grid">
                            <div>Temp</div><div>{sr.weather.temperature_c}°C</div>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </LayerGroup>
            </Overlay>

            {/* Events */}
            <Overlay checked name="Live Events">
              <LayerGroup>
                {topEvents.map((ev) => {
                  const col = ev.display?.color || '#00e5b0';
                  const isSelected = ev.id === selectedEventId;
                  return (
                    <CircleMarker
                      key={ev.id}
                      center={[ev.location.lat, ev.location.lon]}
                      radius={isSelected ? 7 : 4}
                      pathOptions={{
                        color: col,
                        fillColor: col,
                        fillOpacity: isSelected ? 0.85 : 0.6,
                        weight: isSelected ? 2 : 1,
                      }}
                      eventHandlers={{ click: () => onSelectEvent(ev.id) }}
                    >
                      <Popup maxWidth={300}>
                        <div className="signal-popup">
                          <div className="signal-popup-head">
                            <span className="signal-popup-dot" style={{ background: col, boxShadow: `0 0 8px ${col}` }} />
                            <span className="signal-popup-name">{ev.title}</span>
                          </div>
                          <div className="signal-popup-state" style={{ color: col }}>
                            {ev.alert} · {ev.category}
                          </div>
                          {ev.severity != null && (
                            <div className="signal-popup-grid">
                              <div>Severity</div><div>{(ev.severity * 100).toFixed(0)}%</div>
                              {ev.origin && (<><div>Origin</div><div>{ev.origin}</div></>)}
                            </div>
                          )}
                          {ev.reason && <div className="signal-popup-reason">{ev.reason}</div>}
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </LayerGroup>
            </Overlay>

            {/* Karbon AOIs (legacy Mangrove Watch layer) */}
            <Overlay name="Karbon AOIs (Legacy: Mangrove Watch)">
              <LayerGroup>
                {mangroveAois.map((a) => {
                  const col = STATE_COLORS[a.stress_level] || '#00e5b0';
                  return (
                    <Polygon
                      key={a.id}
                      positions={a.polygon}
                      pathOptions={{
                        color: col,
                        fillColor: col,
                        fillOpacity: 0.18,
                        weight: 2,
                        dashArray: '4 4',
                      }}
                    >
                      <Popup maxWidth={320}>
                        <div className="signal-popup">
                          <div className="signal-popup-head">
                            <span className="signal-popup-dot" style={{ background: col, boxShadow: `0 0 8px ${col}` }} />
                            <span className="signal-popup-name">{a.name}</span>
                          </div>
                          <div className="signal-popup-state" style={{ color: col }}>
                            KARBON · {a.stress_level?.toUpperCase()}
                          </div>
                          <div className="signal-popup-grid">
                            <div>Baseline</div><div>{a.baseline_extent_ha.toLocaleString()} ha</div>
                            <div>Latest</div><div>{a.latest_extent_ha.toLocaleString()} ha</div>
                            <div>Δ</div><div>{a.change_pct >= 0 ? '+' : ''}{a.change_pct.toFixed(2)}%</div>
                            <div>Restore</div><div>{a.restoration_potential?.toUpperCase()}</div>
                            <div>Last pass</div><div>{a.last_sat_pass || '—'}</div>
                          </div>
                          {a.drivers?.length > 0 && (
                            <div className="signal-popup-reason">Drivers: {a.drivers.join(', ')}</div>
                          )}
                          {a.notes && <div className="signal-popup-ts">{a.notes}</div>}
                        </div>
                      </Popup>
                    </Polygon>
                  );
                })}
              </LayerGroup>
            </Overlay>
          </LayersControl>

          <FitBounds regions={regions || []} />
        </MapContainer>

        <StateLegend />
        {!owmKey && (
          <div className="map-overlay-note" data-testid="map-overlay-note">
            Environmental overlays (clouds/wind/precip/temp) require an OpenWeatherMap key. Add <code>REACT_APP_OWM_KEY</code> to enable.
          </div>
        )}
      </div>
    </div>
  );
}
